import { Alert, Button, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  StateBadge,
  useColumns,
} from "../../components/ui";
import { orderState } from "../../components/ui/statusBadge";
import { api, type OrderSummary } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";

export function OrderList() {
  const [orders, setOrders] = useState<OrderSummary[]>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function load() {
    try {
      setError(undefined);
      const { orders: loaded } = await api.orders();
      setOrders(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load orders.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const unbilled = orders?.filter((order) => !order.bill && order.status !== "CANCELLED");

  /** Units across the order, which is what a warehouse cares about. */
  const unitsOf = (order: OrderSummary) =>
    order.lineItems.reduce((n, line) => n + line.quantityFulfilled, 0);

  const columns = useColumns<OrderSummary>(
    () => [
      {
        key: "Order",
        header: "Order",
        width: "160px",
        render: (order) => (
          <Text fz={13} fw={600} className="num" truncate>
            {order.reference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (order) => (
          <Text fz={14} truncate>
            {order.customer.name}
          </Text>
        ),
      },
      {
        key: "Raised",
        header: "Raised",
        width: "120px",
        render: (order) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(order.createdAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
            })}
          </Text>
        ),
      },
      {
        key: "Kind",
        header: "Kind",
        width: "150px",
        render: (order) => (
          <Text fz={13} c="gray.7" truncate>
            {order.type.replace(/_/g, " ").toLowerCase()}
          </Text>
        ),
      },
      {
        key: "Units",
        header: "Units",
        width: "90px",
        align: "right",
        render: (order) => (
          <Text fz={14} className="num">
            {unitsOf(order)}
          </Text>
        ),
      },
      {
        key: "Status",
        header: "Status",
        width: "150px",
        render: (order) => <StateBadge state={orderState(order.status) ?? "ordCreated"} />,
      },
      {
        key: "Bill",
        header: "Bill",
        width: "200px",
        // NOT billed is the thing to scan for on this screen - it is the step
        // that moves stock - so it is words rather than an empty cell.
        render: (order) =>
          order.bill ? (
            <Text fz={13} className="num" truncate>
              {order.bill.reference} · {formatINR(order.bill.totalAmount)}
            </Text>
          ) : (
            <Text fz={13} fw={600} c="yellow.8">
              Not billed
            </Text>
          ),
      },
    ],
    [],
  );

  return (
    <Page title="Orders" subtitle="Most recent first" back="/sales" width="wide">
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!orders && !error && <LoadingSkeleton variant="card" count={4} />}

      {orders?.length === 0 && (
        <EmptyState
          title="No orders yet"
          description="Confirm one from Day Orders."
          action={<Button onClick={() => navigate("/sales/queue")}>Open the queue</Button>}
        />
      )}

      {unbilled && unbilled.length > 0 && (
        <Alert
          variant="light"
          color="yellow"
          radius="md"
          icon={<IconAlertTriangle size={20} stroke={1.8} />}
        >
          {unbilled.length} order{unbilled.length === 1 ? "" : "s"} still to be
          billed. Stock only moves when the bill is generated.
        </Alert>
      )}

      {orders && orders.length > 0 && (
        <ListView
          rows={orders}
          columns={columns}
          rowKey={(order) => order.id}
          onOpen={(order) => navigate(`/sales/bill/${order.id}`)}
          exportDataset="orders"
          caption="Orders"
          footnote="Billing is the step that moves stock."
          renderCard={(order) => (
        <RecordCard
          key={order.id}
          to={`/sales/bill/${order.id}`}
          primary={order.reference}
          secondary={order.customer.name}
          badges={
            <>
              <StateBadge state={orderState(order.status) ?? "ordCreated"} />
              {order.bill && (
                <StateBadge
                  state="ordBilled"
                  label={`${order.bill.reference} · ${formatINR(order.bill.totalAmount)}`}
                />
              )}
            </>
          }
          meta={
            <>
              {new Date(order.createdAt).toLocaleDateString()} ·{" "}
              {order.type.replace(/_/g, " ").toLowerCase()}
              {order.preOrder ? ` · from ${order.preOrder.reference}` : ""}
              {/* A service replacement has no day order, and saying nothing
                  reads as missing data rather than as a service job. It used
                  to say "warranty replacement" for every one of these, which
                  was wrong twice over: warranty is not judged in this app any
                  more, and a pro-rata replacement is not free. */}
              {order.serviceJob
                ? ` · ${
                    order.type === "PRORATA" ? "pro-rata" : "free"
                  } replacement, from ${order.serviceJob.reference}`
                : ""}
              {` · ${order.lineItems.reduce(
                (n, line) => n + line.quantityFulfilled,
                0,
              )} unit(s) across ${order.lineItems.length} line(s)`}
            </>
          }
        />
          )}
        />
      )}
    </Page>
  );
}
